import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button";
import { Field } from "./Field";
import { Input } from "./Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";

const OBSERVATION_OPTIONS = [
  { value: "bubbling", label: "Bubbling" },
  { value: "fizzing", label: "Fizzing" },
  { value: "fish_showing", label: "Fish showing" },
  { value: "slick", label: "Slick / feeding sign" },
  { value: "bird_activity", label: "Bird activity" },
  { value: "other", label: "Other" },
];

const OBSERVATION_LABELS = Object.fromEntries(
  OBSERVATION_OPTIONS.map((item) => [item.value, item.label]),
);

function clockLabel(value: unknown) {
  if (!value) return "";
  const date = new Date(String(value));
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SwimObservations({
  sessionId,
}: {
  sessionId: number | string;
}) {
  const queryClient = useQueryClient();
  const observationsQuery = useQuery({
    queryKey: ["carpintel_swim_observations", String(sessionId)],
    queryFn: () =>
      fetch(`/app-api/sessions/${sessionId}/swim-observations`).then((r) => {
        if (!r.ok) throw new Error("Could not load swim observations");
        return r.json();
      }),
  });

  const observations: any[] = observationsQuery.data ?? [];
  const [open, setOpen] = useState(false);
  const [observationType, setObservationType] = useState("bubbling");
  const [locationNote, setLocationNote] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function saveObservation() {
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(
        `/app-api/sessions/${sessionId}/swim-observations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            observation_type: observationType,
            location_note: locationNote.trim() || null,
            notes: notes.trim() || null,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Could not save swim observation");
      await queryClient.invalidateQueries({
        queryKey: ["carpintel_swim_observations", String(sessionId)],
      });
      setLocationNote("");
      setNotes("");
      setOpen(false);
      setMessage(
        `${OBSERVATION_LABELS[observationType] || "Observation"} saved at ${clockLabel(data.occurred_at)}.`,
      );
    } catch (err) {
      setMessage(
        err instanceof Error ? err.message : "Could not save swim observation",
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeObservation(id: number) {
    const response = await fetch(
      `/app-api/sessions/${sessionId}/swim-observations/${id}`,
      { method: "DELETE" },
    );
    const data = await response.json();
    if (!response.ok) {
      setMessage(data.error || "Could not remove swim observation");
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: ["carpintel_swim_observations", String(sessionId)],
    });
    setMessage("Swim observation removed.");
  }

  return (
    <section className="rounded-lg border border-border bg-raised p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">
            Swim observations
          </h2>
          <p className="mt-1 text-sm leading-5 text-secondary">
            Log what you actually see in your swim. CatchCompass can then connect
            observations to later rod moves, setup changes and catches.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? "Cancel" : "+ Swim observation"}
        </Button>
      </div>

      {open ? (
        <div className="mt-4 space-y-3 rounded-lg bg-inset p-3">
          <Field label="What did you see?" id="swim-observation-type">
            <Select
              value={observationType}
              onValueChange={setObservationType}
            >
              <SelectTrigger id="swim-observation-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OBSERVATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Where? (optional)" id="swim-observation-location">
            <Input
              id="swim-observation-location"
              value={locationNote}
              onChange={(e) => setLocationNote(e.target.value)}
              placeholder="e.g. left margin, between the islands"
              maxLength={160}
            />
          </Field>
          <Field label="Note (optional)" id="swim-observation-note">
            <Input
              id="swim-observation-note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. bubbling steadily for 10 minutes"
              maxLength={240}
            />
          </Field>
          <Button
            type="button"
            size="sm"
            onClick={saveObservation}
            isLoading={saving}
          >
            Save observation
          </Button>
        </div>
      ) : null}

      {message ? (
        <p className="mt-3 text-sm text-secondary" role="status">
          {message}
        </p>
      ) : null}

      {observations.length ? (
        <div className="mt-4 divide-y divide-border">
          {observations.slice(0, 12).map((observation: any) => (
            <div
              key={observation.id}
              className="flex items-start justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-primary">
                  {OBSERVATION_LABELS[observation.observation_type] ||
                    observation.observation_type}
                  {observation.location_note
                    ? ` · ${observation.location_note}`
                    : ""}
                </p>
                <p className="mt-0.5 text-xs text-secondary">
                  {clockLabel(observation.occurred_at)}
                  {observation.notes ? ` · ${observation.notes}` : ""}
                </p>
              </div>
              <Button
                type="button"
                size="xs"
                variant="tertiary"
                onClick={() => removeObservation(Number(observation.id))}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-sm text-secondary">
          No swim observations logged yet.
        </p>
      )}
    </section>
  );
}
